import Home from 'pages/home'
import { routes } from './routes'
import StoPage from 'pages/sto/StoPage'
import type { Project } from 'models/project'
import { useProjects } from 'hooks/useProjects'
import { useSsrData } from 'context/SsrDataContext'
import { useEffect, useMemo, useState } from 'react'
import { legacyTabToPath } from 'constants/stoAnchors'
import { StokrLoader } from '@stokr/components-library'
import { RESERVED_TOP_SEGMENTS } from 'router/reservedRoutes'
import { Setup2FAFlow } from 'shared/components/2FA/setup-2fa-flow'
import { Fallback, FallbackErrorBoundary } from 'components/Fallback'
import { getPathAfterStoSlug, normalizeStoSlugForPath } from 'router/stoPaths'
import { Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

/**
 * Shared shell for `/`, `/login`, `/signup`, and `/setup-2fa`.
 * Navigating between these URLs must NOT remount Home — otherwise the signup/login
 * modal closes and reopens (and any typed form values are lost).
 */
function HomeAuthLayout() {
  const { pathname } = useLocation()
  return (
    <>
      {/* Boundary only, no Suspense: Home's lazy sections need the stale-chunk reload,
          but an extra Suspense here would change how the SSR shell streams. */}
      <FallbackErrorBoundary>
        <Home isLogin={pathname === '/login'} isSignUp={pathname === '/signup'} />
      </FallbackErrorBoundary>
      {pathname === '/setup-2fa' && <Setup2FAFlow />}
      <Outlet />
    </>
  )
}

/**
 * Tabs are anchors now. An old `/:slug/trading` link still has to land on the right
 * block, so it redirects to `/:slug#trading`; tabs whose content was removed land on
 * the page top rather than a dead anchor. The server issues a 301 for the same paths
 * — this covers in-app navigation and any request SSR did not handle.
 */
function LegacyStoTabRedirect() {
  const { stoSlug = '', '*': splat = '' } = useParams<{ stoSlug?: string; '*': string }>()
  const { search } = useLocation()
  const slug = normalizeStoSlugForPath(stoSlug)
  const tab = String(splat).split('/').filter(Boolean)[0] ?? ''
  return <Navigate to={legacyTabToPath(slug, tab, search)} replace />
}

/**
 * Gate for `/:stoSlug` deep links. Canonicalises the URL slug and redirects to
 * `/page-not-found` once the allowlist (`public/stos.txt`) and public projects API
 * both confirm the slug is unknown. Renders the page immediately otherwise, so the
 * layout can paint while it loads its own data (hooks/sto).
 */
interface StoRouteLayoutProps {
  allowlistReady: boolean
  allowlistSlugs: Set<string>
  projects: Project[] | null
  projectsReady: boolean
}

function StoRouteLayout({ allowlistReady, allowlistSlugs, projects, projectsReady }: StoRouteLayoutProps) {
  const { stoSlug = '' } = useParams<{ stoSlug?: string }>()
  const { pathname, search } = useLocation()
  const ssrStoDetails = useSsrData('stoDetails')

  const rawFromParams = String(stoSlug).replace(/^\/+/, '')
  const canonical = normalizeStoSlugForPath(rawFromParams)

  const apiSlugs = useMemo(() => {
    const list = (projects ?? []) as Project[]
    return new Set(list.map((p) => normalizeStoSlugForPath(p?.name)).filter(Boolean))
  }, [projects])

  if (RESERVED_TOP_SEGMENTS.has(canonical)) {
    return <Navigate to="/page-not-found" replace />
  }

  if (rawFromParams !== canonical) {
    const suffix = getPathAfterStoSlug(pathname)
    return <Navigate to={`/${canonical}${suffix}${search}`} replace />
  }

  // SSR seeds the project for the current URL synchronously (see ssr-data-loaders.js) — trust
  // it immediately so the page's <PageMeta> renders real OG tags on the first server-flushed
  // HTML, without waiting on the allowlist fetch / projects API (neither runs during SSR).
  const ssrProject = ssrStoDetails.project
  const ssrMatches = Boolean(ssrProject) && normalizeStoSlugForPath(ssrProject?.name) === canonical

  const knownInAllowlist = allowlistSlugs.has(canonical)
  const knownInApi = projectsReady && apiSlugs.has(canonical)
  const known = ssrMatches || knownInAllowlist || knownInApi

  // Only 404 after both sources settle; until then paint the layout and let the page load.
  if (!known && allowlistReady && projectsReady) {
    return <Navigate to="/page-not-found" replace />
  }

  // The page owns its own data via hooks/sto.
  return (
    <Fallback>
      <Outlet />
    </Fallback>
  )
}

const AppRouter = () => {
  const projectsQuery = useProjects({ enabled: true })
  const projects = projectsQuery.data ?? null
  const projectsReady = !projectsQuery.isPending

  const [allowlistSlugs, setAllowlistSlugs] = useState<Set<string>>(() => new Set<string>())
  const [allowlistReady, setAllowlistReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    const loadStoAllowlist = async () => {
      try {
        // BASE_URL is Vite's equivalent of CRA's PUBLIC_URL, which was undefined
        // here and resolved to a literal `/undefined/stos.txt`.
        const res = await fetch(`${import.meta.env.BASE_URL}stos.txt`)
        if (!res.ok) throw new Error(`stos.txt ${res.status}`)

        // The catch-all answers 200 HTML for any wrong path, so res.ok alone
        // would let a whole page through as allowlist entries.
        const contentType = res.headers.get('content-type') ?? ''
        if (contentType.includes('html')) throw new Error(`stos.txt served HTML (${contentType})`)

        const text = await res.text()
        const next = new Set<string>(
          text
            .split(/\r?\n/)
            .map((line) => normalizeStoSlugForPath(line))
            .filter(Boolean),
        )
        if (!cancelled) setAllowlistSlugs(next)
      } catch (error) {
        console.error('Error loading STO list:', error)
      } finally {
        if (!cancelled) setAllowlistReady(true)
      }
    }
    loadStoAllowlist()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Routes>
      {/* One Home instance for home + auth modal URLs (avoids signup/login modal remount flash). */}
      <Route element={<HomeAuthLayout />}>
        <Route path="/" element={null} />
        <Route path="/login" element={null} />
        <Route path="/signup" element={null} />
        <Route path="/setup-2fa" element={null} />
      </Route>
      {routes.map((route) => (
        <Route
          key={route.to}
          path={route.to}
          element={<Fallback fallback={route.isRemote ? <StokrLoader /> : null}>{route.children}</Fallback>}
        />
      ))}

      {/* Dynamic STO deep-link. Static routes above win for non-STO paths.
          `StoRouteLayout` canonicalises the slug and redirects to /page-not-found
          once allowlist + API confirm the slug is unknown.
          Use index + splat (not per-tab paths) so `/admin/team` etc. keep matching
          the federation remotes instead of `/:stoSlug/team`. */}
      <Route
        path="/:stoSlug"
        element={
          <StoRouteLayout
            allowlistReady={allowlistReady}
            allowlistSlugs={allowlistSlugs}
            projects={projects}
            projectsReady={projectsReady}
          />
        }>
        <Route index element={<StoPage />} />
        <Route path="*" element={<LegacyStoTabRedirect />} />
      </Route>
    </Routes>
  )
}

export default AppRouter
